import React, { Fragment, memo, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import style from 'js/components/Prescription/DraggableIntervals/VerticalHandle/VerticalHandle.module.less';
import NumberUtils from 'js/helpers/NumberUtils';
import { usePrescriptionJob } from 'js/components/Prescription/PrescriptionJobContext';
import useEvent from 'js/hooks/useEvent';
import { Box } from '@mui/material';
import DragHandle from 'js/components/Prescription/DragHandle/DragHandle';
import { useHookRef } from '../../../../hooks/useHookRef';
import { getIntervalDisplayBounds, getEffectiveSoilValues } from '../../PrescriptionUtils';
import { isSoilLayer } from '../../PrescriptionJob';
import { getNullIntervalLayout } from '../../nullValueRegions';

const VerticalHandle = ({
  index,
  wrapperRect,
  interval,
  onDragInterval,
  onDragEnd,
  isBulkPrescription,
  appliedInitialIntervals,
  bulkPrescriptionJobs,
}) => {
  const { prescriptionJob } = usePrescriptionJob();
  const { intervals } = prescriptionJob;

  const [prevIntervals, setPrevIntervals] = useState(prescriptionJob.intervals);
  const prevIntervalsRef = useHookRef(prevIntervals);

  const [mouseDown, setMouseDown] = useState(false);
  const [dragPosition, setDragPosition] = useState(null);
  const pendingClientX = useRef(null);
  const frameId = useRef(null);
  const lastDraggedValue = useRef(null);

  const onStartDrag = useCallback(() => {
    lastDraggedValue.current = null;
    setDragPosition(null);
    setPrevIntervals(prescriptionJob.intervals);
    setMouseDown(true);
  }, [prescriptionJob.intervals]);

  const onMousemove = useCallback(
    (clientX) => {
      if (!wrapperRect || !mouseDown || !onDragInterval) {
        return;
      }

      const pIntervals = prevIntervalsRef.current;

      const { nullWidth, normalMin: min } = getNullIntervalLayout(pIntervals);
      const max = pIntervals[pIntervals.length - 1].max;
      const span = max - min;
      const minWidth = span / 20; // 5% min width

      const wrapperLeft = wrapperRect.left;
      const wrapperWidth = wrapperRect.width;

      const interval = pIntervals[index];
      const nextInterval = pIntervals[index + 1];

      // map the position to the entire interval range
      const value = NumberUtils.map(
        clientX,
        wrapperLeft + (wrapperWidth * nullWidth) / 100,
        wrapperLeft + wrapperWidth,
        min,
        max
      );

      // limit the value to avoid overlaps with adjacent intervals
      if (
        value > interval.min + minWidth &&
        value < nextInterval.max - minWidth &&
        value !== lastDraggedValue.current
      ) {
        lastDraggedValue.current = value;
        setDragPosition(((clientX - wrapperLeft) / wrapperWidth) * 100);
        onDragInterval(index, value);
      }
    },
    [index, mouseDown, onDragInterval, wrapperRect, prevIntervalsRef]
  );

  const flushPendingMove = useCallback(() => {
    if (frameId.current !== null) {
      window.cancelAnimationFrame(frameId.current);
      frameId.current = null;
    }
    if (pendingClientX.current !== null) {
      const clientX = pendingClientX.current;
      pendingClientX.current = null;
      onMousemove(clientX);
    }
  }, [onMousemove]);

  const handleMousemove = useCallback(
    (event) => {
      pendingClientX.current = event.clientX;
      if (frameId.current !== null) return;
      frameId.current = window.requestAnimationFrame(() => {
        frameId.current = null;
        flushPendingMove();
      });
    },
    [flushPendingMove]
  );

  useEffect(() => {
    if (mouseDown) {
      window.addEventListener('mousemove', handleMousemove);
      return () => {
        window.removeEventListener('mousemove', handleMousemove);
        if (frameId.current !== null) window.cancelAnimationFrame(frameId.current);
        frameId.current = null;
        pendingClientX.current = null;
      };
    }
  }, [mouseDown, handleMousemove]);

  useEvent(
    'mouseup',
    useCallback(() => {
      if (mouseDown) {
        flushPendingMove();
        if (onDragEnd) onDragEnd();
      }
      setMouseDown(false);
      setDragPosition(null);
      setPrevIntervals(null);
    }, [mouseDown, onDragEnd, flushPendingMove])
  );

  // current position
  const isBulkMode = isBulkPrescription && !appliedInitialIntervals;
  const effectiveValues = useMemo(
    () =>
      isSoilLayer(prescriptionJob.layer)
        ? getEffectiveSoilValues(
            prescriptionJob.values,
            prescriptionJob.layer,
            isBulkMode,
            bulkPrescriptionJobs
          )
        : null,
    [prescriptionJob.values, prescriptionJob.layer, isBulkMode, bulkPrescriptionJobs]
  );
  const bounds = getIntervalDisplayBounds(intervals, index, effectiveValues);
  const left = dragPosition === null ? bounds.left + bounds.width : dragPosition;

  return (
    <Fragment>
      <Box
        className={style.Line}
        sx={{
          left: `${left}%`,
        }}
      />

      <DragHandle
        movementDirection={'horizontal'}
        movementPercent={`${left}%`}
        onStartDrag={onStartDrag}
        disabled={isSoilLayer(prescriptionJob.layer)}
      />
    </Fragment>
  );
};

VerticalHandle.propTypes = {
  onDragInterval: PropTypes.func,
  onDragEnd: PropTypes.func,
  index: PropTypes.number,
  interval: PropTypes.object,
  wrapperRect: PropTypes.any,
};

export default memo(VerticalHandle);
